import { Component, Input, Output, EventEmitter, OnInit, OnDestroy } from '@angular/core';
import { BaseCellComponent } from '../base-cell.component';
import { ViewManager, DataEntryFieldMapping } from 'src/app/board/pages/admin/admin-components/custom-views-config/view-builder/models/view-builder.models';
import { DynamicTableService } from 'src/app/services/dynamic-table.service';
import { DatasetService } from 'src/app/services/dataset.service';
import { Dataset, DataTableSchema, FILE_STORAGE_REFERENCE_ID } from 'src/app/services/models/datasets.models';
import { Subject, takeUntil, bufferTime, filter, mergeMap, EMPTY, of, from } from 'rxjs';

interface DropdownOption {
  label: string;
  value: any;
}

interface ReferencePicklistRequest {
  fieldName: string;
  referenceDataId: number;
  referenceFieldId: number | null;
  fieldToDisplay: string;
}

@Component({
  selector: 'bhub-data-entry-cell',
  standalone: false,
  templateUrl: './data-entry-cell.component.html',
  styleUrl: './data-entry-cell.component.scss'
})
export class DataEntryCellComponent extends BaseCellComponent implements OnInit, OnDestroy {
  @Input() viewManager: ViewManager | null = null;
  @Output() contextDataChange = new EventEmitter<{[contextKey: string]: any}>();

  // Configuration properties
  targetDatasetId: number | null = null;
  fieldMappings: DataEntryFieldMapping[] = [];
  submitButtonText: string = 'Submit';
  clearAfterSubmit: boolean = true;

  // Dataset schema
  datasetSchema: DataTableSchema[] = [];
  dataset: Dataset | null = null;
  availableDatasets: Map<number, Dataset> = new Map();

  // Form values - for free input fields
  formValues: { [fieldName: string]: any } = {};

  // Reference field picklists
  referencePicklists: { [fieldName: string]: DropdownOption[] } = {};

  // Debounced reference picklist loading to prevent duplicate requests
  private loadReferencePicklist$ = new Subject<ReferencePicklistRequest>();
  private loadedPicklistKeys = new Set<string>();

  // State
  private destroy$ = new Subject<void>();
  isSubmitting = false;
  isLoadingSchema = false;

  constructor(
    private dynamicTableService: DynamicTableService,
    private datasetService: DatasetService
  ) {
    super();

    // Set up buffered reference picklist loading to prevent duplicate requests from multiple instances
    this.loadReferencePicklist$
      .pipe(
        bufferTime(100), // Collect all emissions over 100ms window
        filter(requests => requests.length > 0), // Skip empty buffers
        mergeMap((requests) => {
          // Filter out duplicate requests in the buffer
          const uniqueRequests = requests.filter((request, index, self) => {
            const picklistKey = `${request.fieldName}-${request.referenceDataId}-${request.fieldToDisplay}`;
            return index === self.findIndex(r =>
              `${r.fieldName}-${r.referenceDataId}-${r.fieldToDisplay}` === picklistKey
            );
          });

          // Process each unique request
          return from(uniqueRequests);
        }),
        mergeMap((request) => {
          const picklistKey = `${request.fieldName}-${request.referenceDataId}-${request.fieldToDisplay}`;

          // Don't reload if already loaded by this instance
          if (this.loadedPicklistKeys.has(picklistKey)) {
            return EMPTY;
          }

          this.loadedPicklistKeys.add(picklistKey);

          if (request.fieldToDisplay) {
            const referenceFieldName = request.referenceFieldId ? this.getReferenceFieldName(request.referenceDataId, request.referenceFieldId) : '';

            return this.dynamicTableService.getDisplayValuesForReference(
              request.referenceDataId,
              referenceFieldName,
              request.fieldToDisplay
            ).pipe(
              takeUntil(this.destroy$),
              mergeMap(response => {
                const primaryKeyFieldName = this.getPrimaryKeyField(request.referenceDataId);
                const options = response.rowData.map(item => {
                  const rawValue = item[primaryKeyFieldName] || item[Object.keys(item)[0]];
                  const value = rawValue?.toString() || rawValue;
                  return {
                    label: item[request.fieldToDisplay] || '',
                    value: value
                  };
                });

                return of({ fieldName: request.fieldName, options });
              })
            );
          } else {
            return this.dynamicTableService.getValuesForDatasetPrimaryKey(request.referenceDataId).pipe(
              takeUntil(this.destroy$),
              mergeMap(picklist => {
                const options = picklist.values.map(value => ({
                  label: value,
                  value: value
                }));
                return of({ fieldName: request.fieldName, options });
              })
            );
          }
        }),
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: (result: any) => {
          this.referencePicklists[result.fieldName] = result.options;
        },
        error: (error) => {
          console.error('Error loading reference picklist:', error);
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  protected initializeCell(): void {
    this.updateConfiguration();
    this.loadDatasetSchema();
  }

  protected onCellOrDataChange(): void {
    this.updateConfiguration();
    this.loadDatasetSchema();
  }

  protected onCellUpdate(): void {
    this.updateConfiguration();
    this.loadDatasetSchema();
  }

  protected onDataSet(data: any): void {
    // Data entry cells don't typically respond to external data changes
  }

  reloadData(): void {
    // Override reloadData to prevent reloading schema when context changes
    // Only update configuration (which updates context-dependent field values)
    this.updateConfiguration();
  }

  private updateConfiguration(): void {
    if (!this.cell) return;

    this.targetDatasetId = this.getConfigValue('targetDatasetId', null);
    this.fieldMappings = this.getConfigValue('fieldMappings', []);
    this.submitButtonText = this.getConfigValue('submitButtonText', 'Submit');
    this.clearAfterSubmit = this.getConfigValue('clearAfterSubmit', true);

    // Initialize form values for free input fields
    this.initializeFormValues();
  }

  private initializeFormValues(): void {
    this.formValues = {};
    this.fieldMappings.forEach(mapping => {
      if (mapping.inputSource === 'free') {
        this.formValues[mapping.fieldName] = '';
      }
    });
  }

  private loadDatasetSchema(): void {
    if (!this.targetDatasetId) {
      this.datasetSchema = [];
      this.dataset = null;
      return;
    }

    this.isLoadingSchema = true;
    this.datasetService.getDatasetReferences()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (datasetsMap: Map<number, Dataset>) => {
          this.availableDatasets = datasetsMap;
          this.dataset = datasetsMap.get(this.targetDatasetId!) || null;
          if (this.dataset) {
            this.datasetSchema = this.dataset.dataTableSchemas
              .sort((a, b) => (a.ordinal || 0) - (b.ordinal || 0));
            // Load reference picklists for fields with reference data
            this.loadReferencePicklists();
          } else {
            this.datasetSchema = [];
          }
          this.isLoadingSchema = false;
        },
        error: (error) => {
          console.error('Error loading dataset schema:', error);
          this.datasetSchema = [];
          this.dataset = null;
          this.isLoadingSchema = false;
        }
      });
  }

  private loadReferencePicklists(): void {
    this.fieldMappings.forEach(mapping => {
      // Load reference data for both 'free' (dropdown) and 'context' (display value lookup) fields
      if (mapping.inputSource === 'free' || mapping.inputSource === 'context') {
        const fieldSchema = this.datasetSchema.find(s => s.fieldName === mapping.fieldName);

        if (fieldSchema && fieldSchema.referenceDataId &&
            fieldSchema.referenceDataId !== 0 &&
            fieldSchema.referenceDataId !== FILE_STORAGE_REFERENCE_ID) {

          this.loadReferencePicklist$.next({
            fieldName: mapping.fieldName,
            referenceDataId: fieldSchema.referenceDataId,
            referenceFieldId: fieldSchema.referenceFieldId,
            fieldToDisplay: fieldSchema.fieldToDisplay || ''
          });
        }
      }
    });
  }

  private getReferenceFieldName(datasetId: number, fieldId: number): string {
    // Get the reference dataset to find the field name by fieldId
    const referenceDataset = this.availableDatasets.get(datasetId);
    const field = referenceDataset?.dataTableSchemas.find(schema => schema.fieldId === fieldId);
    return field?.fieldName || '';
  }

  private getPrimaryKeyField(datasetId: number): string {
    // Get the reference dataset to find the primary key field
    const referenceDataset = this.availableDatasets.get(datasetId);
    const primaryKeyField = referenceDataset?.dataTableSchemas.find(schema => schema.isPrimaryKey);
    return primaryKeyField?.fieldName || referenceDataset?.datasetReferenceTable.primaryKey || '';
  }

  onSubmit(): void {
    if (!this.targetDatasetId || this.isSubmitting) {
      return;
    }

    // Build the entry data from field mappings
    const entryData = this.buildEntryData();

    if (!entryData) {
      alert('Please fill in all required fields');
      return;
    }

    this.isSubmitting = true;
    this.dynamicTableService.addEntries(this.targetDatasetId, [entryData])
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (success) => {
          this.isSubmitting = false;
          if (success) {
            alert('Entry added successfully!');
            if (this.clearAfterSubmit) {
              this.clearForm();
            }
          } else {
            alert('Error adding entry');
          }
        },
        error: (error) => {
          this.isSubmitting = false;
          console.error('Error adding entry:', error);
          alert('Error adding entry');
        }
      });
  }

  private buildEntryData(): any | null {
    const entryData: any = {};

    for (const mapping of this.fieldMappings) {
      let value: any;

      switch (mapping.inputSource) {
        case 'free':
          value = this.formValues[mapping.fieldName];
          break;

        case 'static':
          value = mapping.staticValue;
          break;

        case 'context':
          if (!mapping.contextObjectKey || !mapping.contextFieldName || !this.viewManager) {
            console.warn(`Missing context configuration for field ${mapping.fieldName}`);
            continue;
          }
          const contextObject = this.viewManager.contextData[mapping.contextObjectKey];
          if (contextObject) {
            if (Array.isArray(contextObject) && contextObject.length > 0) {
              value = contextObject[0][mapping.contextFieldName];
            } else if (typeof contextObject === 'object' && contextObject !== null) {
              value = contextObject[mapping.contextFieldName];
            }
          }
          break;

        case 'null':
          value = null;
          break;
      }

      // Set the value
      // For 'null' input source, explicitly set to null
      // For others, allow empty strings but not undefined/null for required fields
      if (mapping.inputSource === 'null') {
        entryData[mapping.fieldName] = null;
      } else {
        entryData[mapping.fieldName] = value !== undefined && value !== null ? value : '';
      }
    }

    return entryData;
  }

  private clearForm(): void {
    // Clear form values for free input fields
    Object.keys(this.formValues).forEach(key => {
      this.formValues[key] = '';
    });
  }

  getFieldLabel(mapping: DataEntryFieldMapping): string {
    if (mapping.label) {
      return mapping.label;
    }

    // Find the field in the schema to get its display name
    const schemaField = this.datasetSchema.find(s => s.fieldName === mapping.fieldName);
    return schemaField?.displayName || mapping.fieldName;
  }

  getFieldPlaceholder(mapping: DataEntryFieldMapping): string {
    return mapping.placeholder || `Enter ${this.getFieldLabel(mapping)}`;
  }

  // Check if a field should be visible (hide Hidden_Internal_PrimaryKey)
  isFieldVisible(mapping: DataEntryFieldMapping): boolean {
    // Hide Hidden_Internal_PrimaryKey field
    if (mapping.fieldName === 'Hidden_Internal_PrimaryKey') {
      return false;
    }
    return true; // Show all other fields
  }

  // Check if a field is editable
  isFieldEditable(mapping: DataEntryFieldMapping): boolean {
    // Only free input fields are editable
    return mapping.inputSource === 'free';
  }

  // Get the display value for static/context/null fields
  getFieldValue(mapping: DataEntryFieldMapping): string {
    if (mapping.inputSource === 'static') {
      return mapping.staticValue !== undefined && mapping.staticValue !== null ? String(mapping.staticValue) : '';
    } else if (mapping.inputSource === 'context') {
      if (!mapping.contextObjectKey || !mapping.contextFieldName || !this.viewManager) {
        return '';
      }
      const contextObject = this.viewManager.contextData[mapping.contextObjectKey];
      if (contextObject) {
        let value: any;
        if (Array.isArray(contextObject) && contextObject.length > 0) {
          value = contextObject[0][mapping.contextFieldName];
        } else if (typeof contextObject === 'object' && contextObject !== null) {
          value = contextObject[mapping.contextFieldName];
        }

        // If this field has reference data, look up the display value
        const fieldSchema = this.datasetSchema.find(s => s.fieldName === mapping.fieldName);
        if (fieldSchema && fieldSchema.referenceDataId && fieldSchema.fieldToDisplay && value !== undefined && value !== null) {
          const displayValue = this.getDisplayValueForReference(fieldSchema.referenceDataId, fieldSchema.fieldToDisplay, value);
          if (displayValue) {
            return displayValue;
          }
        }

        return value !== undefined && value !== null ? String(value) : '';
      }
    } else if (mapping.inputSource === 'null') {
      return '(null - auto-generated)';
    }
    return '';
  }

  // Look up the display value for a reference field given its ID
  private getDisplayValueForReference(referenceDataId: number, fieldToDisplay: string, referenceValue: any): string | null {
    // Check if we have picklist options loaded for this field's mapping
    // We need to find the field mapping that corresponds to this field
    const mapping = this.fieldMappings.find(m => {
      const fieldSchema = this.datasetSchema.find(s => s.fieldName === m.fieldName);
      return fieldSchema && fieldSchema.referenceDataId === referenceDataId;
    });

    if (!mapping || !this.referencePicklists[mapping.fieldName]) {
      return null;
    }

    // Look up the display value in the loaded picklist options
    const options = this.referencePicklists[mapping.fieldName];
    const matchingOption = options.find(opt => opt.value == referenceValue); // Use == for loose comparison

    return matchingOption ? matchingOption.label : null;
  }

  onFieldValueChange(fieldName: string): void {
    // Field value changed - could add validation here
  }

  // Check if a field has reference data (should show as dropdown)
  hasReferenceData(mapping: DataEntryFieldMapping): boolean {
    if (mapping.inputSource !== 'free') {
      return false; // Only free input fields can be dropdowns
    }

    const fieldSchema = this.datasetSchema.find(s => s.fieldName === mapping.fieldName);
    return !!(fieldSchema && fieldSchema.referenceDataId &&
              fieldSchema.referenceDataId !== 0 &&
              fieldSchema.referenceDataId !== FILE_STORAGE_REFERENCE_ID);
  }

  // Get dropdown options for a field
  getDropdownOptions(mapping: DataEntryFieldMapping): DropdownOption[] {
    return this.referencePicklists[mapping.fieldName] || [];
  }
}
